import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import RequirementForm from '../components/RequirementForm';
import Pagination from '../components/Pagination';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { ResizableTable, ResizableHeader } from '../components/ResizableTable';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Requirements() {
  const { projectKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [project, setProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'functional',
    priority: 'medium',
    status: 'draft',
    folderId: '',
    tags: '',
  });

  // ── JIRA state ────────────────────────────────────────────────────────────
  const [jiraModal, setJiraModal] = useState(null); // { reqId, reqTitle, mode: 'link'|'create' }
  const [jiraIssueKey, setJiraIssueKey] = useState('');
  const [jiraProjectKey, setJiraProjectKey] = useState('');
  const [jiraIssueType, setJiraIssueType] = useState('Story');
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraError, setJiraError] = useState('');
  // ── Rename state ──────────────────────────────────────────────────────────
  const [renameModal, setRenameModal] = useState(null); // { reqId, title }
  const [renameValue, setRenameValue] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  // ── Sync-all state ────────────────────────────────────────────────────────
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  // ── Active action dropdown ────────────────────────────────────────────────
  const [openActionMenu, setOpenActionMenu] = useState(null); // reqId
  // ──────────────────────────────────────────────────────────────────────────

  const REQ_COLS = [
    { key: 'id',      defaultWidth: 100 },
    { key: 'title',   defaultWidth: 300 },
    { key: 'type',    defaultWidth: 110 },
    { key: 'priority',defaultWidth: 100 },
    { key: 'status',  defaultWidth: 100 },
    { key: 'folder',  defaultWidth: 140 },
    { key: 'jira',    defaultWidth: 160 },
    { key: 'actions', defaultWidth: 70 },
  ];
  const { colWidths: reqColWidths, onMouseDown: reqMouseDown, tableRef: reqTableRef } = useResizableColumns('zipqa-requirements-cols', REQ_COLS);

  const folderId = searchParams.get('folderId');

  const [projectId, setProjectId] = useState(null);

  useEffect(() => {
    if (projectKey) {
      fetchProject();
    }
  }, [projectKey]);

  useEffect(() => {
    if (projectId) {
      setCurrentPage(1);
      fetchFolders();
      fetchRequirements();
    }
  }, [projectId, folderId]);

  useEffect(() => {
    if ((currentPage > 1 || pageSize !== 50) && projectId) {
      fetchRequirements();
    }
  }, [currentPage, pageSize, projectId]);

  const fetchProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const projectData = data.data || data;
        if (projectData) {
          setProject(projectData);
          setProjectId(projectData.id || projectData._id);
        }
      }
    } catch (err) {
      console.error('Error loading project:', err);
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/folders?projectId=${projectId}&type=requirement`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setFolders(data.data || []);
      }
    } catch (err) {
      console.error('Error loading folders:', err);
    }
  };

  const fetchRequirements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API_URL}/requirements?projectId=${projectId}&page=${currentPage}&limit=${pageSize}`;
      if (folderId) url += `&folderId=${folderId}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setRequirements(data.data || []);
        if (data.pagination) {
          setTotalItems(data.pagination.total || 0);
          setTotalPages(data.pagination.pages || 1);
        }
      }
    } catch (err) {
      console.error('Error loading requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const url = editingReq
        ? `${API_URL}/requirements/${editingReq.id || editingReq._id}`
        : `${API_URL}/requirements`;
      const method = editingReq ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        projectId,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingReq(null);
        setFormData({
          title: '',
          description: '',
          type: 'functional',
          priority: 'medium',
          status: 'draft',
          folderId: '',
          tags: '',
        });
        setError('');
        fetchRequirements();
      } else {
        const data = await response.json();
        let errorMessage = data.error || 'Failed to save requirement';
        if (data.details) {
          errorMessage += ': ' + data.details;
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError('Error saving requirement: ' + err.message);
      console.error(err);
    }
  };

  const handleEdit = req => {
    setEditingReq(req);
    setFormData({
      title: req.title,
      description: req.description || '',
      type: req.type,
      priority: req.priority,
      status: req.status,
      folderId: req.folderId?.id || req.folderId?._id || req.folderId || '',
      tags: Array.isArray(req.tags) ? req.tags.join(', ') : '',
    });
    setShowModal(true);
  };

  const handleDelete = async reqId => {
    if (!window.confirm('Are you sure you want to delete this requirement?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/requirements/${reqId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        setError('');
        fetchRequirements();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete requirement');
      }
    } catch (err) {
      setError('Error deleting requirement: ' + err.message);
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRequirements.length === 0) {
      setError('No requirements selected');
      return;
    }
    if (!window.confirm(`Delete ${selectedRequirements.length} requirement(s)? This cannot be undone.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      let successCount = 0;
      for (const reqId of selectedRequirements) {
        try {
          const response = await fetch(`${API_URL}/requirements/${reqId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            successCount++;
          }
        } catch (err) {
          console.error('Failed to delete requirement:', err);
        }
      }
      setSelectedRequirements([]);
      fetchRequirements();
      setError(`Deleted ${successCount} requirement(s)`);
      setTimeout(() => setError(''), 3000);
    } catch (err) {
      setError('Failed to delete requirements');
    }
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/requirements/export/csv?projectId=${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `requirements-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
      alert('Requirements exported successfully!');
      setError('');
    } catch (err) {
      alert('Failed to export requirements: ' + (err.message || 'Unknown error'));
      console.error(err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // CSV Template with headers and sample data
    const templateData = `ID,Title,Description,Type,Priority,Status,Folder,Tags,JIRA Issue Key,JIRA URL,JIRA Status
,"Example: User Authentication","Verify user authentication functionality","functional","high","draft","Authentication","security; login",,
,"Example: Data Validation","Ensure data validation rules are enforced","functional","medium","draft","Validation","data; input","PROJ-123","https://jira.example.com/browse/PROJ-123","In Progress"
,"Example: Performance SLA","System should respond within 200ms","non-functional","high","draft","Performance","performance; sla",,`;

    // Create blob and download
    const blob = new Blob([templateData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'requirements-template.csv');
    document.body.appendChild(link);
    link.click();
    link.parentElement.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    try {
      setImportLoading(true);
      const fileContent = await importFile.text();
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/requirements/import/csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          csvData: fileContent,
          projectId: projectId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Import failed');
      }

      // Store import results to display as alerts
      setImportResult({
        type: 'success',
        count: data.imported,
        warnings: data.warnings || [],
        errors: data.errors || []
      });
      
      setShowImportModal(false);
      setImportFile(null);
      fetchRequirements();
      setError('');
    } catch (err) {
      setImportResult({
        type: 'error',
        message: 'Failed to import requirements: ' + (err.message || 'Unknown error'),
        errors: [],
        warnings: []
      });
      setShowImportModal(false);
    } finally {
      setImportLoading(false);
    }
  };

  // ── JIRA handlers ─────────────────────────────────────────────────────────
  const openJiraLink = (req) => {
    setJiraModal({ reqId: req.id || req._id, reqTitle: req.title, mode: 'link' });
    setJiraIssueKey('');
    setJiraError('');
  };

  const openJiraCreate = (req) => {
    setJiraModal({ reqId: req.id || req._id, reqTitle: req.title, mode: 'create' });
    setJiraProjectKey('');
    setJiraIssueType('Story');
    setJiraError('');
  };

  const closeJiraModal = () => {
    setJiraModal(null);
    setJiraIssueKey('');
    setJiraProjectKey('');
    setJiraError('');
    setJiraLoading(false);
  };

  const handleJiraLink = async () => {
    if (!jiraIssueKey.trim()) { setJiraError('Enter a JIRA issue key'); return; }
    setJiraLoading(true);
    setJiraError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/jira/requirements/${jiraModal.reqId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ issueKey: jiraIssueKey.trim().toUpperCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Link failed');
      closeJiraModal();
      fetchRequirements();
    } catch (err) {
      setJiraError(err.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraCreate = async () => {
    if (!jiraProjectKey.trim()) { setJiraError('Enter a JIRA project key'); return; }
    setJiraLoading(true);
    setJiraError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/jira/requirements/${jiraModal.reqId}/create-issue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ projectKey: jiraProjectKey.trim().toUpperCase(), issueType: jiraIssueType }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Create failed');
      closeJiraModal();
      fetchRequirements();
    } catch (err) {
      setJiraError(err.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleJiraUnlink = async (reqId) => {
    if (!window.confirm('Unlink this JIRA issue?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/jira/requirements/${reqId}/link`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchRequirements();
    } catch (err) {
      setError('Failed to unlink: ' + err.message);
    }
  };

  const handleJiraSync = async (req) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/jira/requirements/${req.id || req._id}/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      fetchRequirements();
    } catch (err) {
      setError('JIRA sync failed: ' + err.message);
    }
  };

  const handleRename = async () => {
    if (!renameValue.trim()) return;
    setRenameLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/requirements/${renameModal.reqId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: renameValue.trim() }),
      });
      if (!res.ok) throw new Error('Rename failed');
      setRenameModal(null);
      fetchRequirements();
    } catch (err) {
      setError('Rename failed: ' + err.message);
    } finally {
      setRenameLoading(false);
    }
  };

  const handleSyncAll = async () => {
    const linked = requirements.filter(r => r.jira?.issueKey);
    if (linked.length === 0) { setError('No JIRA-linked requirements to sync'); return; }
    setSyncAllLoading(true);
    let synced = 0;
    for (const req of linked) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`${API_URL}/jira/requirements/${req.id || req._id}/sync`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        synced++;
      } catch (err) {
        console.error('Sync failed for', req.title, err);
      }
    }
    setSyncAllLoading(false);
    fetchRequirements();
    setError(`Synced ${synced} of ${linked.length} JIRA-linked requirement(s)`);
    setTimeout(() => setError(''), 4000);
  };

  const getJiraStatusBadge = (status) => {
    if (!status) return null;
    const lower = status.toLowerCase();
    let cls = 'bg-secondary';
    if (lower.includes('done') || lower.includes('closed') || lower.includes('resolved')) cls = 'bg-success';
    else if (lower.includes('progress') || lower.includes('review')) cls = 'bg-primary';
    else if (lower.includes('blocked')) cls = 'bg-danger';
    return <span className={`badge ${cls} ms-1`} style={{ fontSize: '10px' }}>{status}</span>;
  };
  // ──────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div>
        <div className="content-header">
          <h2>Requirements</h2>
        </div>
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showModal ? (
        <RequirementForm
          requirement={editingReq}
          folders={folders}
          onSubmit={handleSubmit}
          onCancel={() => setShowModal(false)}
          loading={false}
          error={error}
        />
      ) : (
        <div>
      <div className="content-header">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0" style={{ background: 'transparent', padding: 0 }}>
              <li className="breadcrumb-item">
                <Link to="/projects">Projects</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/projects/${projectKey.toLowerCase()}`}>{project?.name}</Link>
              </li>
              <li className="breadcrumb-item active">Requirements</li>
            </ol>
          </nav>
          <h2 style={{ marginTop: '1rem' }}>
            Requirements
            {selectedRequirements.length > 0 && (
              <small className="text-muted ms-3">
                ({selectedRequirements.length} selected)
              </small>
            )}
          </h2>
        </div>
        <div className="btn-group">
          {selectedRequirements.length > 0 && (
            <button 
              className="btn btn-danger" 
              onClick={handleBulkDelete}
              title="Delete selected requirements"
            >
              🗑️ Delete ({selectedRequirements.length})
            </button>
          )}
          <Link 
            to={`/projects/${projectKey}/folders/requirement`}
            className="btn btn-outline-secondary"
            title="Manage folders"
          >
            📁 Manage Folders
          </Link>
          <button
            className="btn btn-outline-secondary"
            onClick={handleSyncAll}
            disabled={syncAllLoading}
            title="Sync all JIRA-linked requirements"
          >
            {syncAllLoading ? (
              <><span className="spinner-border spinner-border-sm me-1" style={{ width: 12, height: 12 }} />Syncing…</>
            ) : '↻ Sync JIRA'}
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowExportModal(true)}
          >
            📥 Export CSV
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowImportModal(true)}
          >
            📤 Import CSV
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + New
          </button>
        </div>
      </div>

      <div className="container-fluid">
        {/* Import Result Alerts */}
        {importResult && (
          <div style={{ marginBottom: '1rem' }}>
            {importResult.type === 'success' && (
              <div className="alert alert-success alert-dismissible fade show" role="alert">
                <strong>Success!</strong> {importResult.count} requirement(s) imported.
                <button type="button" className="btn-close" onClick={() => setImportResult(null)}></button>
              </div>
            )}
            
            {importResult.warnings && importResult.warnings.length > 0 && (
              <div className="alert alert-warning alert-dismissible fade show" role="alert">
                <strong>Warnings:</strong>
                <ul className="mb-0 mt-2">
                  {importResult.warnings.map((warning, idx) => (
                    <li key={idx}>{warning}</li>
                  ))}
                </ul>
                <button type="button" className="btn-close" onClick={() => setImportResult(prev => ({ ...prev, warnings: [] }))}></button>
              </div>
            )}
            
            {importResult.type === 'error' && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>Error:</strong> {importResult.message}
                {importResult.errors && importResult.errors.length > 0 && (
                  <ul className="mb-0 mt-2">
                    {importResult.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                )}
                <button type="button" className="btn-close" onClick={() => setImportResult(null)}></button>
              </div>
            )}
          </div>
        )}

        <ResizableTable tableRef={reqTableRef} className="table-hover table-striped bg-white">
        <thead>
          <tr>
            <ResizableHeader colKey="id" width={reqColWidths.id} onMouseDown={reqMouseDown}>ID</ResizableHeader>
            <ResizableHeader colKey="title" width={reqColWidths.title} onMouseDown={reqMouseDown}>Title</ResizableHeader>
            <ResizableHeader colKey="type" width={reqColWidths.type} onMouseDown={reqMouseDown}>Type</ResizableHeader>
            <ResizableHeader colKey="priority" width={reqColWidths.priority} onMouseDown={reqMouseDown}>Priority</ResizableHeader>
            <ResizableHeader colKey="status" width={reqColWidths.status} onMouseDown={reqMouseDown}>Status</ResizableHeader>
            <ResizableHeader colKey="folder" width={reqColWidths.folder} onMouseDown={reqMouseDown}>Folder</ResizableHeader>
            <ResizableHeader colKey="jira" width={reqColWidths.jira} onMouseDown={reqMouseDown}>JIRA</ResizableHeader>
            <ResizableHeader colKey="actions" width={reqColWidths.actions} onMouseDown={reqMouseDown}>Actions</ResizableHeader>
          </tr>
        </thead>
        <tbody>
          {requirements.map(req => (
            <tr key={req.id || req._id}>
              <td style={{ textAlign: 'center', fontWeight: 600 }}>
                <Link 
                  to={`/projects/${projectKey}/requirements/${req.reqId || req.id || req._id}`}
                  style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}
                >
                  {req.reqId || `REQ-${String(req.id || req._id).slice(-5).padStart(3, '0')}`}
                </Link>
              </td>
              <td className="cell-wrap">
                <strong>{req.title}</strong>
                {req.description && (
                  <div>
                    <small className="text-muted">{req.description.substring(0, 100)}...</small>
                  </div>
                )}
              </td>
              <td>
                <span className="badge bg-info">{req.type}</span>
              </td>
              <td>
                <span
                  className={`badge bg-${
                    req.priority === 'critical'
                      ? 'danger'
                      : req.priority === 'high'
                      ? 'warning'
                      : 'secondary'
                  }`}
                >
                  {req.priority}
                </span>
              </td>
              <td>
                <span className="badge bg-secondary">{req.status}</span>
              </td>
              <td>{req.folderId?.name || '-'}</td>
              <td style={{ minWidth: '120px' }}>
                {req.jira?.issueKey ? (
                  <div>
                    <a
                      href={req.jira.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="fw-semibold text-decoration-none"
                    >
                      🔗 {req.jira.issueKey}
                    </a>
                    {getJiraStatusBadge(req.jira.status)}
                  </div>
                ) : (
                  <span className="text-muted" style={{ fontSize: 12 }}>Not linked</span>
                )}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {/* Actions dropdown */}
                <div
                  className="dropdown"
                  style={{ position: 'relative', display: 'inline-block' }}
                  onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpenActionMenu(null); }}
                  ref={el => {
                    if (el && openActionMenu === (req.id || req._id)) {
                      const menu = el.querySelector('.dropdown-menu');
                      if (menu) {
                        const rect = menu.getBoundingClientRect();
                        const spaceBelow = window.innerHeight - rect.bottom;
                        if (spaceBelow < 0) {
                          menu.style.bottom = '100%';
                          menu.style.top = 'auto';
                        } else {
                          menu.style.top = '100%';
                          menu.style.bottom = 'auto';
                        }
                      }
                    }
                  }}
                >
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    style={{ padding: '3px 10px', fontSize: 13 }}
                    onClick={() => setOpenActionMenu(openActionMenu === (req.id || req._id) ? null : (req.id || req._id))}
                    title="Actions"
                  >
                    ⋯
                  </button>
                  {openActionMenu === (req.id || req._id) && (
                    <ul
                      className="dropdown-menu show"
                      style={{
                        position: 'absolute', right: 0, left: 'auto', top: '100%',
                        zIndex: 1050, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,.12)',
                        marginTop: '4px', maxHeight: '300px', overflowY: 'auto',
                      }}
                    >
                      <li>
                        <button className="dropdown-item" onClick={() => { handleEdit(req); setOpenActionMenu(null); }}>
                          ✏️ Edit
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => {
                          setRenameModal({ reqId: req.id || req._id, title: req.title });
                          setRenameValue(req.title);
                          setOpenActionMenu(null);
                        }}>
                          🏷️ Rename
                        </button>
                      </li>
                      <li><hr className="dropdown-divider" /></li>
                      {req.jira?.issueKey ? (
                        <>
                          <li>
                            <button className="dropdown-item" onClick={() => { openJiraLink(req); setOpenActionMenu(null); }}>
                              🔗 Relink JIRA Issue
                            </button>
                          </li>
                          <li>
                            <button className="dropdown-item text-danger" onClick={() => { handleJiraUnlink(req.id || req._id); setOpenActionMenu(null); }}>
                              🔓 Unlink JIRA
                            </button>
                          </li>
                        </>
                      ) : (
                        <>
                          <li>
                            <button className="dropdown-item" onClick={() => { openJiraLink(req); setOpenActionMenu(null); }}>
                              🔗 Link JIRA Issue
                            </button>
                          </li>
                          <li>
                            <button className="dropdown-item" onClick={() => { openJiraCreate(req); setOpenActionMenu(null); }}>
                              ➕ Create JIRA Issue
                            </button>
                          </li>
                        </>
                      )}
                      <li><hr className="dropdown-divider" /></li>
                      <li>
                        <button className="dropdown-item text-danger" onClick={() => { handleDelete(req.id || req._id); setOpenActionMenu(null); }}>
                          🗑️ Delete
                        </button>
                      </li>
                    </ul>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </ResizableTable>

      {/* Pagination */}
      {requirements.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          limit={pageSize}
          total={totalItems}
          onPageChange={setCurrentPage}
          onLimitChange={(newLimit) => {
            setPageSize(newLimit);
            setCurrentPage(1);
          }}
          isLoading={loading}
        />
      )}

      {requirements.length === 0 && (
        <div className="alert alert-info">No requirements found. Create your first requirement!</div>
      )}
      </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">🏷️ Rename Requirement</h5>
                <button type="button" className="btn-close" onClick={() => setRenameModal(null)} />
              </div>
              <div className="modal-body">
                <label className="form-label">New Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleRename()}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setRenameModal(null)} disabled={renameLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={handleRename} disabled={renameLoading || !renameValue.trim()}>
                  {renameLoading ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</> : 'Rename'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* JIRA Link / Create Modal */}
      {jiraModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {jiraModal.mode === 'link' ? '🔗 Link JIRA Issue' : '➕ Create JIRA Issue'}
                </h5>
                <button type="button" className="btn-close" onClick={closeJiraModal} />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Requirement: <strong>{jiraModal.reqTitle}</strong>
                </p>
                {jiraError && (
                  <div className="alert alert-danger py-2">{jiraError}</div>
                )}
                {jiraModal.mode === 'link' ? (
                  <div>
                    <label className="form-label">JIRA Issue Key</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. PROJ-123"
                      value={jiraIssueKey}
                      onChange={e => setJiraIssueKey(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleJiraLink()}
                    />
                    <small className="text-muted">Enter the exact JIRA issue key to link.</small>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <label className="form-label">JIRA Project Key *</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. PROJ"
                        value={jiraProjectKey}
                        onChange={e => setJiraProjectKey(e.target.value.toUpperCase())}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Issue Type</label>
                      <select
                        className="form-select"
                        value={jiraIssueType}
                        onChange={e => setJiraIssueType(e.target.value)}
                      >
                        <option>Story</option>
                        <option>Task</option>
                        <option>Bug</option>
                        <option>Epic</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeJiraModal} disabled={jiraLoading}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={jiraModal.mode === 'link' ? handleJiraLink : handleJiraCreate}
                  disabled={jiraLoading}
                >
                  {jiraLoading ? (
                    <><span className="spinner-border spinner-border-sm me-2" />Working…</>
                  ) : jiraModal.mode === 'link' ? 'Link Issue' : 'Create Issue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Export Requirements to CSV</h5>
                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>
                  Export {requirements.length} requirement(s) to CSV for backup or further processing.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowExportModal(false)}
                  disabled={exportLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleExportCSV}
                  disabled={exportLoading || requirements.length === 0}
                >
                  {exportLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Requirements from CSV</h5>
                <button type="button" className="btn-close" onClick={() => setShowImportModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p className="text-muted small mb-2">
                    Need help with the format? Download our template CSV file:
                  </p>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleDownloadTemplate}
                  >
                    📋 Download Template
                  </button>
                </div>
                <hr className="my-3" />
                <div className="mb-3">
                  <label htmlFor="csvFile" className="form-label">
                    Select CSV File
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="csvFile"
                    accept=".csv"
                    onChange={e => setImportFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div className="alert alert-info">
                  <small>
                    <strong>CSV Format:</strong> The file should have columns: Title, Description, Type, Priority, Status, Folder, Tags, JIRA Issue Key, JIRA URL, JIRA Status. (Optional columns: ID, Created Date)
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportModal(false)}
                  disabled={importLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleImportCSV}
                  disabled={importLoading || !importFile}
                >
                  {importLoading ? 'Importing...' : 'Import CSV'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Requirements;
